import { gsap } from "gsap";
import {
  clamp,
  ellipseDistance,
  interpolateHotspot,
  isInteractiveTarget,
  pixelDistance,
  projectHotspot
} from "../core/math.js";

function isWithin(time, range) {
  return time >= range[0] && time <= range[1];
}

export class AnimalController {
  #hotspots;
  #store;
  #video;
  #scene;
  #audio;
  #cursor;
  #aura;
  #cue;
  #config;
  #tier;
  #pointer = {
    x: window.innerWidth / 2,
    y: window.innerHeight / 2
  };
  #active = null;
  #hoverStartedAt = 0;
  #hoverTriggeredKey = null;
  #feedbackCounts = new Map();
  #rateTween = null;
  #cleanup = [];
  #debug = null;
  #lastOverlapCheck = 0;
  #cueFocusId = null;
  #cueFocusUntil = 0;
  #cueAnimalId;

  constructor({
    hotspots,
    store,
    video,
    scene,
    audio,
    cursor,
    aura,
    config,
    tier,
    cue = null
  }) {
    this.#hotspots = hotspots;
    this.#store = store;
    this.#video = video;
    this.#scene = scene;
    this.#audio = audio;
    this.#cursor = cursor;
    this.#aura = aura;
    this.#cue = cue;
    this.#config = config;
    this.#tier = tier;
  }

  init() {
    this.#syncObservationCue(null);
    if (this.#tier === "reduced") {
      return;
    }
    if (this.#cue) {
      const cueHandler = (event) => {
        event.preventDefault();
        this.triggerObservationCue();
      };
      this.#cue.addEventListener("click", cueHandler);
      this.#cleanup.push(() =>
        this.#cue.removeEventListener("click", cueHandler)
      );
    }
    const moveHandler = (event) => {
      this.#pointer.x = event.clientX;
      this.#pointer.y = event.clientY;
    };
    const clickHandler = (event) => this.#onClick(event);
    window.addEventListener("pointermove", moveHandler, { passive: true });
    window.addEventListener("pointerdown", clickHandler);
    this.#cleanup.push(
      () => window.removeEventListener("pointermove", moveHandler),
      () => window.removeEventListener("pointerdown", clickHandler)
    );
    this.#cleanup.push(
      this.#store.on("media:loop", () => {
        this.#feedbackCounts.clear();
        this.#hoverTriggeredKey = null;
      }),
      this.#store.on("performance:downgrade", (event) => {
        this.#tier = event.detail.performance.tier;
      })
    );

    if (
      import.meta.env.DEV &&
      new URLSearchParams(window.location.search).has("hotspots")
    ) {
      this.#createDebugTools();
    }
  }

  #getActiveHotspots(time) {
    return this.#hotspots
      .filter((animal) => isWithin(time, animal.window))
      .map((animal) => {
        const source = interpolateHotspot(animal.keyframes, time);
        return {
          animal,
          source,
          projected: projectHotspot(
            source,
            window.innerWidth,
            window.innerHeight
          )
        };
      });
  }

  #chooseNearest(activeHotspots, pointer = this.#pointer) {
    let selected = null;
    let minimum = Infinity;
    for (const entry of activeHotspots) {
      const distance = ellipseDistance(pointer, entry.projected);
      if (distance < minimum) {
        minimum = distance;
        selected = { ...entry, ellipseDistance: distance };
      }
    }
    return selected;
  }

  #distanceThresholds() {
    const factor = clamp(
      0.75,
      Math.min(window.innerWidth, window.innerHeight) / 900,
      1.25
    );
    return {
      inner: this.#config.innerDistance * factor,
      outer: this.#config.outerDistance * factor
    };
  }

  update(timestamp) {
    const state = this.#store.get();
    if (
      state.dialog.projectId ||
      state.lifecycle === "static-fallback" ||
      document.hidden
    ) {
      this.#deactivate();
      return null;
    }

    const time = this.#video.element.currentTime;
    const activeHotspots = this.#getActiveHotspots(time);
    if (!activeHotspots.length) {
      this.#deactivate();
      this.#updateDebug(null, time);
      return null;
    }

    const observation = this.#selectObservation(activeHotspots);
    this.#syncObservationCue(observation);
    const cueFocused =
      timestamp < this.#cueFocusUntil
        ? activeHotspots.find(
            (entry) => entry.animal.id === this.#cueFocusId
          )
        : null;
    if (!cueFocused) {
      this.#cueFocusId = null;
      this.#cueFocusUntil = 0;
    }
    const selected =
      cueFocused ?? this.#chooseNearest(activeHotspots);
    const thresholds = this.#distanceThresholds();
    const centerDistance = pixelDistance(
      this.#pointer,
      selected.projected
    );
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    let phase = "none";
    let strength = 0;

    if (cueFocused) {
      phase = "focus";
      strength = 1;
      this.#hoverStartedAt = timestamp;
    } else if (!coarse && centerDistance <= thresholds.outer) {
      phase = "approach";
      strength = 1 - centerDistance / thresholds.outer;
    }
    if (!cueFocused && !coarse && centerDistance <= thresholds.inner) {
      phase = "focus";
      strength = 0.62 + (1 - centerDistance / thresholds.inner) * 0.38;
    }
    if (!cueFocused && !coarse && selected.ellipseDistance <= 1) {
      phase = "focus";
      strength = 1;
      const key = `${state.media.loopIndex}:${selected.animal.id}`;
      if (this.#active?.animal.id !== selected.animal.id) {
        this.#hoverStartedAt = timestamp;
        this.#hoverTriggeredKey = null;
      } else if (
        timestamp - this.#hoverStartedAt >=
          selected.animal.hoverDelayMs &&
        this.#hoverTriggeredKey !== key
      ) {
        this.#hoverTriggeredKey = key;
        this.#triggerFeedback(selected, "hover");
      }
    } else {
      this.#hoverStartedAt = timestamp;
    }

    this.#active = selected;
    this.#applyVisualState(selected, phase, strength);
    this.#scene.setAnimalTarget({
      x: selected.projected.x,
      y: selected.projected.y,
      strength
    });
    this.#cursor.setAnimalActive(phase !== "none");
    this.#store.patch(
      {
        animal: {
          activeId: selected.animal.id,
          phase
        }
      },
      "animal:state"
    );

    if (timestamp - this.#lastOverlapCheck > 220) {
      this.#updateCardOverlap(activeHotspots);
      this.#lastOverlapCheck = timestamp;
    }
    this.#updateDebug(selected, time);
    return {
      id: selected.animal.id,
      phase,
      projected: selected.projected,
      strength
    };
  }

  #applyVisualState(selected, phase, strength) {
    const { projected } = selected;
    document.documentElement.style.setProperty(
      "--animal-x",
      `${projected.x}px`
    );
    document.documentElement.style.setProperty(
      "--animal-y",
      `${projected.y}px`
    );
    document.documentElement.style.setProperty(
      "--animal-size-x",
      `${Math.max(projected.rx * 2.4, 100)}px`
    );
    document.documentElement.style.setProperty(
      "--animal-size-y",
      `${Math.max(projected.ry * 2.4, 100)}px`
    );
    document.documentElement.style.setProperty(
      "--animal-visibility",
      String(phase === "none" ? 0 : 0.2 + strength * 0.55)
    );
    document.documentElement.style.setProperty(
      "--animal-scale",
      String(0.82 + strength * 0.2)
    );
    this.#aura.classList.toggle("is-focused", phase === "focus");
  }

  #deactivate() {
    this.#active = null;
    this.#hoverStartedAt = 0;
    this.#cueFocusId = null;
    this.#cueFocusUntil = 0;
    this.#syncObservationCue(null);
    document.documentElement.style.setProperty("--animal-visibility", "0");
    this.#aura.classList.remove("is-focused");
    this.#scene.setAnimalTarget(null);
    this.#cursor.setAnimalActive(false);
    document
      .querySelectorAll(".project-card.is-animal-overlap")
      .forEach((card) => card.classList.remove("is-animal-overlap"));
    const current = this.#store.get().animal;
    if (current.phase !== "none" || current.activeId !== null) {
      this.#store.patch(
        { animal: { activeId: null, phase: "none" } },
        "animal:state"
      );
    }
  }

  #onClick(event) {
    if (
      isInteractiveTarget(event.target) ||
      this.#store.get().dialog.projectId
    ) {
      return;
    }
    const time = this.#video.element.currentTime;
    const active = this.#getActiveHotspots(time);
    const selected = this.#chooseNearest(active, {
      x: event.clientX,
      y: event.clientY
    });
    if (!selected) {
      return;
    }
    const scale =
      window.matchMedia("(pointer: coarse)").matches
        ? this.#config.mobileHitScale
        : 1;
    if (selected.ellipseDistance <= scale) {
      this.#triggerFeedback(selected, "click");
    }
  }

  #selectObservation(activeHotspots) {
    const currentId = this.#cue?.dataset.animalId;
    const current = currentId
      ? activeHotspots.find(
          (entry) => entry.animal.id === currentId
        )
      : null;
    if (current) {
      return current;
    }
    return activeHotspots.reduce((selected, entry) => {
      if (!selected) {
        return entry;
      }
      return (entry.source.strength ?? 1) >
        (selected.source.strength ?? 1)
        ? entry
        : selected;
    }, null);
  }

  #syncObservationCue(entry) {
    if (!this.#cue) {
      return;
    }
    if (entry) {
      const normalizedX = entry.projected.x / window.innerWidth;
      const currentSide = this.#cue.dataset.cueSide;
      const nextSide =
        normalizedX > 0.56
          ? "left"
          : normalizedX < 0.44
            ? "right"
            : currentSide ||
              (normalizedX >= 0.5 ? "left" : "right");
      this.#cue.dataset.cueSide = nextSide;
    } else {
      this.#cue.removeAttribute("data-cue-side");
    }
    const nextAnimalId = entry?.animal.id ?? null;
    if (
      this.#cueAnimalId === nextAnimalId &&
      this.#cue.hidden === !entry
    ) {
      return;
    }
    this.#cueAnimalId = nextAnimalId;
    this.#store.patch(
      { animal: { cueVisible: Boolean(entry) } },
      "animal:cue"
    );
    if (!entry) {
      this.#cue.hidden = true;
      this.#cue.classList.remove("is-visible");
      this.#cue.removeAttribute("data-animal-id");
      this.#cue.setAttribute("aria-label", "观察林间动物");
      const title = this.#cue.querySelector(
        "[data-animal-cue-label], strong"
      );
      const detail = this.#cue.querySelector("em");
      if (title) {
        title.textContent = this.#config.cueText ?? "林间有动静";
      }
      if (detail) {
        detail.textContent = "留意光影";
      }
      if (!title && !detail) {
        this.#cue.textContent =
          this.#config.cueText ?? "林间有动静";
      }
      return;
    }
    const title = this.#cue.querySelector(
      "[data-animal-cue-label], strong"
    );
    const detail = this.#cue.querySelector("em");
    if (title) {
      title.textContent = this.#config.cueText ?? "林间有动静";
    }
    if (detail) {
      detail.textContent = `发现${entry.animal.label}`;
    }
    if (!title && !detail) {
      this.#cue.textContent = `${
        this.#config.cueText ?? "林间有动静"
      } · ${entry.animal.label}`;
    }
    this.#cue.dataset.animalId = entry.animal.id;
    this.#cue.setAttribute(
      "aria-label",
      `观察林间的${entry.animal.label}`
    );
    this.#cue.hidden = false;
    this.#cue.classList.add("is-visible");
  }

  triggerObservationCue() {
    const state = this.#store.get();
    if (
      this.#tier === "reduced" ||
      state.dialog.projectId ||
      state.lifecycle === "static-fallback" ||
      document.hidden
    ) {
      this.#syncObservationCue(null);
      return false;
    }
    const activeHotspots = this.#getActiveHotspots(
      this.#video.element.currentTime
    );
    const selected = this.#selectObservation(activeHotspots);
    if (!selected) {
      this.#syncObservationCue(null);
      return false;
    }
    this.#syncObservationCue(selected);
    this.#cueFocusId = selected.animal.id;
    this.#cueFocusUntil = performance.now() + 900;
    this.#triggerFeedback(selected, "cue");
    return true;
  }

  #triggerFeedback(entry, source) {
    const state = this.#store.get();
    const loopKey = `${state.media.loopIndex}:${entry.animal.id}`;
    const count = this.#feedbackCounts.get(loopKey) ?? 0;
    const inBestWindow = isWithin(
      this.#video.element.currentTime,
      entry.animal.bestWindow
    );
    const strong =
      inBestWindow &&
      (entry.source.strength ?? 1) >= 0.68 &&
      count < entry.animal.maxStrongFeedbackPerLoop;

    if (strong) {
      this.#feedbackCounts.set(loopKey, count + 1);
    }
    this.#aura.classList.remove("is-triggered");
    void this.#aura.offsetWidth;
    this.#aura.classList.add("is-triggered");
    this.#scene.burstWind(entry.animal.id === "deer" ? 0.48 : 0.22);
    this.#audio.accent(entry.animal.id === "deer" ? 0.035 : 0.02);

    if (strong) {
      this.#rateTween?.kill();
      this.#rateTween = gsap
        .timeline()
        .to(this.#video.element, {
          playbackRate: this.#config.slowRate,
          duration: 0.2,
          ease: "sine.out"
        })
        .to(this.#video.element, {
          playbackRate: this.#config.slowRate,
          duration: this.#config.slowHoldMs / 1000
        })
        .to(this.#video.element, {
          playbackRate: 0.7,
          duration: 0.32,
          ease: "sine.inOut"
        });
    }

    if (
      source === "click" &&
      this.#tier === "low" &&
      navigator.vibrate
    ) {
      navigator.vibrate(entry.animal.id === "deer" ? [16, 30, 12] : 14);
    }
    this.#store.patch(
      {
        animal: {
          activeId: entry.animal.id,
          phase: "triggered"
        }
      },
      "animal:trigger"
    );
    this.#store.emit("animal:feedback", {
      id: entry.animal.id,
      source,
      strong,
      loopIndex: state.media.loopIndex
    });
  }

  #updateCardOverlap(activeHotspots) {
    const projectedRects = activeHotspots.map(({ projected }) => ({
      left: projected.x - projected.rx,
      right: projected.x + projected.rx,
      top: projected.y - projected.ry,
      bottom: projected.y + projected.ry
    }));
    document.querySelectorAll(".project-card").forEach((card) => {
      const rect = card.getBoundingClientRect();
      const overlaps = projectedRects.some(
        (hotspot) =>
          rect.left < hotspot.right &&
          rect.right > hotspot.left &&
          rect.top < hotspot.bottom &&
          rect.bottom > hotspot.top
      );
      card.classList.toggle("is-animal-overlap", overlaps);
    });
  }

  #createDebugTools() {
    const panel = document.createElement("aside");
    panel.className = "hotspot-debug";
    panel.innerHTML = `
      <strong>HOTSPOT CALIBRATION</strong>
      <output data-debug-output>等待视频</output>
      <input data-debug-time type="range" min="0" max="31.7" step="0.05" value="0" aria-label="视频调试时间">
      <div data-debug-ellipse></div>
    `;
    document.body.append(panel);
    const input = panel.querySelector("[data-debug-time]");
    const inputHandler = () => this.#video.setCurrentTime(Number(input.value));
    input.addEventListener("input", inputHandler);
    this.#cleanup.push(() => input.removeEventListener("input", inputHandler));
    this.#debug = {
      panel,
      output: panel.querySelector("[data-debug-output]"),
      input,
      ellipse: panel.querySelector("[data-debug-ellipse]")
    };
  }

  #updateDebug(selected, time) {
    if (!this.#debug) {
      return;
    }
    this.#debug.input.value = String(time);
    if (!selected) {
      this.#debug.output.textContent = `${time.toFixed(2)}s · no animal`;
      this.#debug.ellipse.hidden = true;
      return;
    }
    const { source, projected, animal } = selected;
    this.#debug.output.textContent =
      `${time.toFixed(2)}s · ${animal.id} · ` +
      `(${source.cx.toFixed(3)}, ${source.cy.toFixed(3)})`;
    this.#debug.ellipse.hidden = false;
    Object.assign(this.#debug.ellipse.style, {
      left: `${projected.x - projected.rx}px`,
      top: `${projected.y - projected.ry}px`,
      width: `${projected.rx * 2}px`,
      height: `${projected.ry * 2}px`
    });
  }

  getDebugSnapshot() {
    return this.#active
      ? {
          id: this.#active.animal.id,
          source: this.#active.source,
          projected: this.#active.projected,
          feedbackCounts: Object.fromEntries(this.#feedbackCounts)
        }
      : null;
  }

  destroy() {
    this.#cleanup.forEach((cleanup) => cleanup());
    this.#rateTween?.kill();
    this.#debug?.panel.remove();
    this.#deactivate();
  }
}
